import { IClient } from '../../../../Assembler/dist/registry/iface/iClient.js';
import type { ITransientStore } from '../../../../Assembler/dist/registry/iface/iTransientStore.js';
import type { GetParentJsonValueHandler } from '../../../../Assembler/dist/registry/iface/getParentJsonValueHandler.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { TimeFilter } from '../../registryCommon/dynamicData/timeFilter.js';

/**
 * Purpose:
 * THE client handler for the time capability — the client-tier counterpart of TimeData, exactly as
 * ScenarioClient is for ScenariosData and CounterClient is for CounterData. ONE type serves BOTH lanes;
 * the template declaration decides which (PLAN_HANDLER_FIX §15.2, §3.7(5)).
 *
 * Responsibilities:
 * - Project the SERVER instant carried by its TimeClientData island when the declaration names that
 *   preparer via data-client-data (the frozen ServerTime witness)
 * - Resolve the CURRENT instant from the tier it runs on when the declaration names no preparer and the
 *   binding declares DataSource "Pure" (the advancing CurrentTime)
 * - Format either through the shared TimeFilter, into the param-declared TimeKey
 *
 * Registered on the SEPARATE IClient registry (distinct from the server preparer TimeClientData). The same
 * body runs in the browser bundle and in the console --apiclient/--ociclient simulation, certifying the
 * browser artifact by construction.
 *
 * ⭐ On the PURE lane this handler READS THE CLOCK, which is the whole point: CurrentTime advances on every
 * client refresh while the island-fed ServerTime beside it stays frozen. Rendering both side by side is the
 * observable proof this tier executed locally — a byte compare cannot show it (§15.3 D6.3). Both values come
 * from THIS type bound twice with different params, mirroring how the server binds one TimeData twice.
 */
export class TimeClient extends IClient {
    /**
     * Gets the full type name for client-handler operation tracking.
     */
    get typeName(): string {
        return 'TimeClient';
    }

    /**
     * SOURCE-AGNOSTIC: formats an instant under the declared DateFormat into the declared TimeKey. Which
     * instant depends solely on where the raw came from — the §3.7(5) contract, "same handler, same filter,
     * only the raw's origin differs":
     *
     * - island lane (declaration names a preparer): project the instant the SERVER resolved. The parsed
     *   instant IS the server's wall clock in components, so the browser's own zone never enters the
     *   render. Frozen across client refreshes, so it stays byte-comparable.
     * - pure lane (no data-client-data; binding declares DataSource "Pure"): READ THE CLOCK on this tier.
     *
     * The pure lane's only pin is an optional FixedTime on the BINDING's param. There is no per-request
     * harness override: adding one would be production behaviour that exists solely to serve test
     * infrastructure. A harness that needs determinism normalises the value out on its own side.
     */
    process(appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, transientStore: ITransientStore, configJson: JsonObject | null, getParentJsonValue: GetParentJsonValueHandler | null, rawJson: JsonObject): JsonObject {
        void appSite; void appView; void appFile; void componentName; void componentDepth; void resolvedAppSite; void resolvedComponentName; void componentJson; void transientStore; void configJson; void getParentJsonValue;
        const timeKey = TimeFilter.readTimeKey(paramJson);

        // ISLAND LANE: a raw instant was handed in, so report what the SERVER resolved and never touch a
        // clock here. The DateFormat rides the island so the witness is byte-identical to the server render.
        const parsed = TimeFilter.parseRawTime(rawJson);
        if (parsed !== null) {
            return TimeFilter.buildTimeResult(timeKey, TimeFilter.formatCurrentTime(parsed.instant, parsed.dateFormat));
        }

        // PURE LANE: no island, so this tier resolves the instant itself. The binding is the ONLY source of a
        // pin — there is deliberately no per-request harness override, since a test hook here would be
        // production behaviour existing to serve test infrastructure.
        const dateFormat = TimeFilter.readDateFormat(paramJson);
        const isLocalTime = TimeFilter.readIsLocalTime(paramJson);
        const fixedTime = TimeFilter.readFixedTime(paramJson);

        const instant = TimeFilter.resolveCurrentTime(fixedTime, isLocalTime);
        return TimeFilter.buildTimeResult(timeKey, TimeFilter.formatCurrentTime(instant, dateFormat));
    }
}
